import { isAxiosError } from 'axios';
import Sentry from 'src/services/sentry';

import { galenErrorKey } from 'src/utils/galenErrorKey';

/**
 * Enhanced error reporting utility for Sentry
 * Captures comprehensive error information including Axios-specific details
 * Note: Uses extras for custom messages to maintain proper error grouping
 */
export const captureExceptionWithContext = (
  err: unknown,
  context?: {
    customMessage?: string;
    operation?: string;
    tags?: Record<string, string>;
    extras?: Record<string, string>;
    source?: string;
  },
) => {
  Sentry.withScope((scope) => {
    scope.setLevel('error');

    scope.setTag('error_type', 'general_error');
    scope.setTag('error_source', context?.source || 'unknown');

    // Set custom message as extra (not changing the error message to maintain grouping)
    if (context?.customMessage) {
      scope.setExtra('Custom Error Message', context.customMessage);
    }

    if (context?.operation) {
      scope.setTag('operation', context.operation);
    }

    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context?.extras) {
      Object.entries(context.extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    // Enhanced error context for Axios errors
    if (isAxiosError(err)) {
      const { response, request, config, code, message } = err;

      // Set Axios-specific tags
      scope.setTag('error_type', 'axios_error');
      scope.setTag('axios_code', code || 'unknown');

      // Add request information
      if (config) {
        scope.setExtra('Request URL', config.url);
        scope.setExtra('Request Method', config.method?.toUpperCase());

        const safeHeaders = {
          'Content-Type': config.headers['Content-Type'],
          Accept: config.headers['Accept'],
          'X-API-Version': config.headers['X-API-Version'],
          'X-APP-TYPE': config.headers['X-APP-TYPE'],
          'X-TENANT-DOMAIN': config.headers['X-TENANT-DOMAIN'],
        };
        scope.setExtra('Request Headers', safeHeaders);

        scope.setExtra('Request Timeout', config.timeout);
        scope.setExtra('Base URL', config.baseURL);
        scope.setExtra('Retry Count', config['axios-retry']?.retryCount ?? 0);
      }

      // Add response information if available
      if (response) {
        scope.setExtra('Response Status', response.status);
        scope.setExtra('Response Status Text', response.statusText);
        scope.setTag('http_status', response.status.toString());
        scope.setTag('http_method', config?.method?.toUpperCase() || 'unknown');

        const errorKey = galenErrorKey(response.data);
        if (errorKey) {
          scope.setTag('galen_error_key', errorKey);
        }
      }

      if (request && !response) {
        scope.setTag('error_category', 'network_error');
      }

      // Add error message and code
      scope.setExtra('Axios Error Message', message);
      scope.setExtra('Axios Error Code', code);

      // Set a more descriptive error message for Sentry (but keep original for grouping)
      const errorMessage = response
        ? `HTTP ${response.status} ${response.statusText}: ${config?.url || 'Unknown URL'}`
        : `Axios Error: ${message} (${code || 'no code'})`;

      scope.setContext('Axios Error Details', {
        message: errorMessage,
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status: response?.status,
        statusText: response?.statusText,
      });
    } else {
      // Handle non-Axios errors
      scope.setTag('error_type', 'non_axios_error');
      if (err instanceof Error) {
        scope.setExtra('Error Name', err.name);
        scope.setExtra('Error Stack', err.stack);
        scope.setContext('Error Details', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });
      }
    }

    Sentry.captureException(err);
  });
};

/**
 * Simple wrapper for backward compatibility
 * Use captureExceptionWithContext for new code
 */
export const captureException = (err: unknown) => {
  captureExceptionWithContext(err);
};

/**
 * Capture error with minimal context - for quick error reporting
 */
export const captureError = (err: unknown, message?: string) => {
  captureExceptionWithContext(err, {
    customMessage: message,
    source: 'quick_capture',
  });
};

/**
 * Capture Axios error with enhanced context - specifically for HTTP errors
 */
export const captureAxiosError = (
  err: unknown,
  context?: {
    customMessage?: string;
    operation?: string;
    tags?: Record<string, string>;
    extras?: Record<string, string>;
  },
) => {
  Sentry.withScope((scope) => {
    // Set level based on status code
    if (isAxiosError(err) && err.response?.status === 404) {
      scope.setLevel('info');
      scope.setTag('error_type', 'not_found');
      scope.setTag('http_status', '404');
    } else {
      scope.setLevel('error');
      scope.setTag('error_type', 'axios_error');
    }

    // Set custom message as extra (not changing the error message to maintain grouping)
    if (context?.customMessage) {
      scope.setExtra('Custom Error Message', context.customMessage);
    }

    // Set operation tag if provided
    if (context?.operation) {
      scope.setTag('operation', context.operation);
    }

    // Set additional tags if provided
    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    // Set additional extras if provided
    if (context?.extras) {
      Object.entries(context.extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    // Enhanced error context for Axios errors
    if (isAxiosError(err)) {
      const { response, request, config, code, message } = err;

      // Add request information
      if (config) {
        scope.setExtra('Request URL', config.url);
        scope.setExtra('Request Method', config.method?.toUpperCase());

        const safeHeaders = {
          'Content-Type': config.headers['Content-Type'],
          Accept: config.headers['Accept'],
          'X-API-Version': config.headers['X-API-Version'],
          'X-APP-TYPE': config.headers['X-APP-TYPE'],
          'X-TENANT-DOMAIN': config.headers['X-TENANT-DOMAIN'],
        };
        scope.setExtra('Request Headers', safeHeaders);

        scope.setExtra('Request Timeout', config.timeout);
        scope.setExtra('Base URL', config.baseURL);
        scope.setExtra('Retry Count', config['axios-retry']?.retryCount ?? 0);
      }

      // Add response information if available
      if (response) {
        scope.setExtra('Response Status', response.status);
        scope.setExtra('Response Status Text', response.statusText);
        scope.setTag('http_status', response.status.toString());
        scope.setTag('http_method', config?.method?.toUpperCase() || 'unknown');

        const errorKey = galenErrorKey(response.data);
        if (errorKey) {
          scope.setTag('galen_error_key', errorKey);
        }
      }

      if (request && !response) {
        scope.setTag('error_category', 'network_error');
      }

      // Add error message and code
      scope.setExtra('Axios Error Message', message);
      scope.setExtra('Axios Error Code', code);

      // Set a more descriptive error message for Sentry (but keep original for grouping)
      const errorMessage = response
        ? `HTTP ${response.status} ${response.statusText}: ${config?.url || 'Unknown URL'}`
        : `Axios Error: ${message} (${code || 'no code'})`;

      scope.setContext('Axios Error Details', {
        message: errorMessage,
        url: config?.url,
        method: config?.method?.toUpperCase(),
        status: response?.status,
        statusText: response?.statusText,
      });
    } else {
      // Handle non-Axios errors
      if (err instanceof Error) {
        scope.setExtra('Error Name', err.name);
        scope.setExtra('Error Stack', err.stack);
        scope.setContext('Error Details', {
          name: err.name,
          message: err.message,
          stack: err.stack,
        });
      }
    }

    Sentry.captureException(err);
  });
};

/**
 * Capture camera-related errors with specific context
 */
export const captureCameraError = (
  err: unknown,
  context?: {
    customMessage?: string;
    operation?: string;
    tags?: Record<string, string>;
    extras?: Record<string, string>;
  },
) => {
  captureExceptionWithContext(err, {
    ...context,
    source: 'camera_error',
    tags: {
      ...context?.tags,
      component: 'camera',
    },
  });
};

/**
 * Capture user action errors - User actions errors, which are not related to HTTP errors
 */
export const captureUserActionError = (
  err: unknown,
  action: string,
  context?: {
    customMessage?: string;
    tags?: Record<string, string>;
    extras?: Record<string, string>;
  },
) => {
  captureExceptionWithContext(err, {
    ...context,
    operation: action,
    source: 'user_action',
    tags: {
      ...context?.tags,
      action_type: action,
    },
  });
};
